import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { Loader2, Sparkles, RefreshCw, PenLine, Target, CheckSquare, Square, Printer, Pencil, Save, X, Plus, Trash2, History } from "lucide-react";

export function weekLabel(ws) {
  if (!ws) return "";
  const d = new Date(`${ws}T00:00:00`);
  return `Week of ${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
}

function PlanEditor({ plan, onSaved, onCancel }) {
  const [items, setItems] = useState(plan.items.map((i) => ({ goal: i.goal, interventions: [...i.interventions] })));
  const [saving, setSaving] = useState(false);
  const setIv = (gi, ii, v) => setItems((it) => it.map((g, i) => i === gi ? { ...g, interventions: g.interventions.map((x, j) => j === ii ? v : x) } : g));
  const removeIv = (gi, ii) => setItems((it) => it.map((g, i) => i === gi ? { ...g, interventions: g.interventions.filter((_, j) => j !== ii) } : g));
  const addIv = (gi) => setItems((it) => it.map((g, i) => i === gi ? { ...g, interventions: [...g.interventions, ""] } : g));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/weekly-plans/${plan.id}`, { items });
      toast.success("Weekly plan updated.");
      onSaved(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <div data-testid="plan-editor" className="space-y-3">
      {items.map((it, gi) => (
        <div key={gi} className="rounded-xl border border-[#DCE5DF] bg-white overflow-hidden">
          <div className="px-4 py-2.5 bg-[#F4F6F3] border-b border-[#DCE5DF]">
            <p className="text-[10px] font-bold text-[#4A6B53] uppercase tracking-wider">Goal {gi + 1}</p>
            <Textarea data-testid={`edit-goal-${gi}`} value={it.goal} rows={2} onChange={(e) => setItems((x) => x.map((g, i) => i === gi ? { ...g, goal: e.target.value } : g))}
              className="mt-1 bg-white border-[#DCE5DF] text-sm font-semibold" />
          </div>
          <div className="p-3 space-y-2">
            {it.interventions.map((iv, ii) => (
              <div key={ii} className="flex gap-2 items-start">
                <Textarea data-testid={`edit-intervention-${gi}-${ii}`} value={iv} rows={2} onChange={(e) => setIv(gi, ii, e.target.value)} className="flex-1 bg-[#F9F9F6] border-[#DCE5DF] text-sm" />
                <button data-testid={`remove-intervention-${gi}-${ii}`} onClick={() => removeIv(gi, ii)} className="h-10 w-10 shrink-0 rounded-full border border-[#E2DFD8] text-[#B0503C] hover:bg-[#FBF2EF] flex items-center justify-center" title="Remove">
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            ))}
            <button data-testid={`add-intervention-${gi}`} onClick={() => addIv(gi)} className="text-xs font-semibold text-[#4A6B53] hover:underline flex items-center gap-1">
              <Plus className="w-3.5 h-3.5" strokeWidth={2} /> Add intervention
            </button>
          </div>
        </div>
      ))}
      <div className="flex justify-end gap-2 pt-1">
        <Button data-testid="cancel-edit-plan-btn" variant="outline" onClick={onCancel} className="rounded-full h-10 border-[#E2DFD8] bg-white"><X className="w-4 h-4 mr-1.5" /> Cancel</Button>
        <Button data-testid="save-plan-btn" onClick={save} disabled={saving} className="rounded-full h-10 bg-[#4A6B53] hover:bg-[#3D5A45] text-white">
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" strokeWidth={1.5} />} Save plan
        </Button>
      </div>
    </div>
  );
}

const doneMap = (plan) => Object.fromEntries(Object.keys(plan?.done || {}).map((k) => [k, true]));

export default function WeeklyPlanPanel({ consumer, canGenerate, onWriteNote, onChanged }) {
  const [plan, setPlan] = useState(null);
  const [weekStart, setWeekStart] = useState("");
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [focus, setFocus] = useState("");
  const [checked, setChecked] = useState({});
  const [editing, setEditing] = useState(false);
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    setLoading(true);
    setChecked({});
    setEditing(false);
    api.get("/weekly-plans", { params: { consumer_id: consumer.id } })
      .then(({ data }) => { setPlan(data.plan); setWeekStart(data.week_start); setChecked(doneMap(data.plan)); })
      .catch(() => setPlan(null))
      .finally(() => setLoading(false));
  }, [consumer.id]);

  const generate = async (carryForward = false) => {
    setGenerating(true);
    try {
      const { data } = await api.post("/weekly-plans/generate", { consumer_id: consumer.id, focus, carry_forward: carryForward });
      setPlan(data);
      setChecked({});
      toast.success(carryForward ? `This week's plan built from last week for ${consumer.name}.` : `Weekly interventions ready for ${consumer.name}.`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setGenerating(false);
    }
  };

  const printPlan = async () => {
    setPrinting(true);
    try {
      const res = await api.get(`/weekly-plans/${plan.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Weekly-Plan-${consumer.name.replace(/\s+/g, "_")}-${weekStart}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Checklist downloaded — print it or keep it on your phone.");
    } catch {
      toast.error("Could not build the checklist.");
    } finally {
      setPrinting(false);
    }
  };

  const toggle = (key) => {
    const next = !checked[key];
    setChecked((c) => ({ ...c, [key]: next }));
    if (plan?.id) {
      api.put(`/weekly-plans/${plan.id}/done`, { key, done: next })
        .then(({ data }) => { setPlan((p) => (p ? { ...p, done: data.done } : p)); onChanged?.(); })
        .catch(() => { setChecked((c) => ({ ...c, [key]: !next })); toast.error("Couldn't save that tick — try again."); });
    }
  };
  const items = plan?.items || [];
  const pickedKeys = Object.keys(checked).filter((k) => checked[k]);

  const writeNote = () => {
    const goals = [], interventions = [];
    items.forEach((it, gi) => {
      const chosen = it.interventions.filter((_, ii) => checked[`${gi}-${ii}`]);
      const use = pickedKeys.length ? chosen : it.interventions;
      if (use.length) { goals.push(it.goal); interventions.push(...use); }
    });
    onWriteNote(consumer, goals, interventions);
  };

  return (
    <div data-testid="weekly-plan-panel" className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] font-bold text-[#4A6B53] uppercase tracking-wider flex items-center gap-1.5">
            <Target className="w-3.5 h-3.5" strokeWidth={1.5} /> This week's interventions · {weekLabel(weekStart)}
          </p>
          {plan?.week_focus && <p className="text-sm text-[#3A453F] mt-1 italic" data-testid="plan-week-focus">{plan.week_focus}</p>}
        </div>
        {plan && !editing && (
          <div className="flex gap-2 flex-wrap">
            <Button data-testid="print-plan-btn" variant="outline" onClick={printPlan} disabled={printing} className="rounded-full h-9 border-[#E2DFD8] bg-white">
              {printing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" strokeWidth={1.5} />} Print checklist
            </Button>
            {canGenerate && (
              <>
                <Button data-testid="edit-plan-btn" variant="outline" onClick={() => setEditing(true)} className="rounded-full h-9 border-[#E2DFD8] bg-white">
                  <Pencil className="w-4 h-4 mr-2" strokeWidth={1.5} /> Edit
                </Button>
                <Button data-testid="regenerate-plan-btn" variant="outline" onClick={() => generate(false)} disabled={generating} className="rounded-full h-9 border-[#4A6B53] text-[#4A6B53]">
                  {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" strokeWidth={1.5} />} Regenerate
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {loading && <p className="text-sm text-[#7A8C82]">Loading…</p>}

      {!loading && !plan && (
        <div className="rounded-xl border border-dashed border-[#DCE5DF] bg-[#F9F9F6] p-5 text-center" data-testid="plan-empty">
          <p className="text-sm text-[#3A453F]">No interventions planned for this week yet.</p>
          {canGenerate ? (
            <div className="mt-3 text-left">
              <label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">Anything to focus on this week? (optional)</label>
              <Textarea data-testid="plan-focus-input" value={focus} onChange={(e) => setFocus(e.target.value)} rows={2}
                placeholder="e.g. Has a job interview Thursday — practice greeting and answering questions." className="mt-1.5 bg-white border-[#DCE5DF] text-sm" />
              <div className="mt-3 flex flex-wrap gap-2">
                <Button data-testid="carry-forward-plan-btn" onClick={() => generate(true)} disabled={generating} className="h-11 px-6 rounded-full bg-[#4A6B53] hover:bg-[#3D5A45] text-white">
                  {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Planning the week…</> : <><History className="w-4 h-4 mr-2" strokeWidth={1.5} /> Start from last week's plans</>}
                </Button>
                <Button data-testid="generate-plan-btn" variant="outline" onClick={() => generate(false)} disabled={generating} className="h-11 px-5 rounded-full border-[#4A6B53] text-[#4A6B53] bg-white">
                  <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.5} /> Fresh plan from the PCP
                </Button>
              </div>
              <p className="text-[11px] text-[#7A8C82] mt-2">"Start from last week" uses last week's plan (what got done) and every staff member's "Plan for Next Week" from their notes.</p>
            </div>
          ) : (
            <p className="text-xs text-[#7A8C82] mt-1">Ask your QP to generate them — then they'll show up here.</p>
          )}
        </div>
      )}

      {!loading && plan && editing && (
        <PlanEditor plan={plan} onCancel={() => setEditing(false)} onSaved={(p) => { setPlan(p); setEditing(false); setChecked({}); onChanged?.(); }} />
      )}

      {!loading && plan && !editing && (
        <>
          <div className="space-y-3">
            {items.map((it, gi) => (
              <div key={gi} data-testid={`plan-goal-${gi}`} className="rounded-xl border border-[#DCE5DF] bg-white overflow-hidden">
                <div className="px-4 py-2.5 bg-[#F4F6F3] border-b border-[#DCE5DF]">
                  <p className="text-[10px] font-bold text-[#4A6B53] uppercase tracking-wider">Goal {gi + 1}</p>
                  <p className="text-sm font-semibold text-[#121814]">{it.goal}</p>
                </div>
                <ul className="divide-y divide-[#EEF2EF]">
                  {it.interventions.map((iv, ii) => {
                    const key = `${gi}-${ii}`;
                    const on = !!checked[key];
                    return (
                      <li key={ii}>
                        <button type="button" data-testid={`plan-intervention-${gi}-${ii}`} onClick={() => toggle(key)}
                          className={`w-full text-left flex items-start gap-3 px-4 py-3 text-sm transition-colors ${on ? "bg-[#EAF1EC] text-[#121814]" : "text-[#3A453F] hover:bg-[#F9F9F6]"}`}>
                          {on ? <CheckSquare className="w-5 h-5 mt-0.5 shrink-0 text-[#4A6B53]" strokeWidth={1.5} /> : <Square className="w-5 h-5 mt-0.5 shrink-0 text-[#B8C4BC]" strokeWidth={1.5} />}
                          <span className="flex-1">{iv}</span>
                          {plan.done?.[key]?.name && <span data-testid={`done-by-${key}`} className="text-[10px] text-[#4A6B53] shrink-0 mt-1">✓ {plan.done[key].name.split(" ")[0]}</span>}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
          {onWriteNote && (
            <div className="flex items-center justify-between gap-3 flex-wrap pt-1">
              <p className="text-xs text-[#7A8C82]">
                {pickedKeys.length ? `${pickedKeys.length} of ${items.reduce((n, it) => n + it.interventions.length, 0)} done this week — ticks are saved for your QP` : "Tap each intervention when you've done it — it's saved for the week. Then write your note."}
              </p>
              <Button data-testid="write-note-from-plan-btn" onClick={writeNote} className="h-11 px-6 rounded-full bg-[#4A6B53] hover:bg-[#3D5A45] text-white">
                <PenLine className="w-4 h-4 mr-2" strokeWidth={1.5} /> Write progress note
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
